import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Paginator } from 'primereact/paginator'
import EmptyState from '../../components/ui/EmptyState'
import FilterBar from '../../components/ui/FilterBar'
import PageHeader from '../../components/ui/PageHeader'
import { useActionStatuses } from '../actions/hooks/useActionStatuses'
import { ACTION_STATUS } from '../actions/types'
import DraftMeetingsCard from './components/DraftMeetingsCard'
import MeetingGroupCard from './components/MeetingGroupCard'
import { useDraftMeetings } from './hooks/useDraftMeetings'
import { useMeetingGroups } from './hooks/useMeetingGroups'
import '../admin/admin.css'
import './meetings.css'

const PAGE_SIZE = 12

/**
 * Grup kartındaki aksiyon özetinde **gösterilen** durumlar (kullanıcı kararı 2026-08-28): Aksiyon
 * Bekleniyor · Tamamlandı · Kapatıldı — yani <b>ana</b> aksiyonun statü kümesi, Arşivlendi hariç
 * (arşivli kayıt zaten backend sayımına girmez).
 *
 * <p>Sıra ve <b>adlar</b> yine REFERANS'tan gelir; burada yalnız hangi id'lerin çizileceği süzülür —
 * ad sabit kodlanmaz (DB katalogu tek kaynak).
 *
 * <p>Bu üç durum, backend'in saydığı kümeyle örtüşür: sayım yalnız <b>ana</b> görevleri kapsar
 * (2026-08-28), dolayısıyla alt göreve özgü 332 "Devam Ediyor" zaten dönmez. Listede karşılığı olmayan
 * bir durum gelirse ({@code GroupActionStats}) çizilmez — "Diğer" kovası kaldırıldı.
 */
const CARD_STATUS_IDS: number[] = [
  ACTION_STATUS.PENDING,
  ACTION_STATUS.COMPLETED,
  ACTION_STATUS.CLOSED,
]

/**
 * Tutanak Takip → **Toplantılar** ekranı (038/041; 044'te iç içe adımlar rota+breadcrumb'a taşındı).
 * Bu sayfa yalnız toplantı grubu **kart grid'i** (ad + açıklama + yayınlanmış toplantı sayısı + aksiyon durum
 * özeti) — <b>salt-okuma</b>, CRUD yok. Karta tıklanınca o grubun toplantı listesine
 * (`/minutes/meeting-minutes/meetings/group/:groupId`) gidilir; oradan bir toplantı seçilince Toplantı Detay açılır.
 * Grup CRUD'u ayrı **"Toplantı Grupları"** ekranındadır.
 *
 * Grup kartları **taslak (360) içermez**; taslaklar grid'in sonundaki sabit **"Taslaklar"** kartından (tüm
 * gruplardan) açılır ve orada tamamlanıp yayınlanır (2026-07-30 analist kararı).
 */
export default function MeetingsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [page, setPage] = useState(0)

  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(search)
      setPage(0)
    }, 300)
    return () => clearTimeout(id)
  }, [search])

  // Boş grup gizlenir: yalnız altında aktif toplantısı olan gruplar (item 1). CRUD ekranı hepsini gösterir.
  const groups = useMeetingGroups({ q: debounced, page, size: PAGE_SIZE, withMeetings: true })
  // Kart aksiyon özetindeki durum adları/sırası REFERANS'tan (sabit kodlanmaz); İptal/Arşiv zaten dışlanır.
  const actionStatuses = useActionStatuses()
  // Kartta yalnız ana görevin üç durumu çizilir (bkz. CARD_STATUS_IDS); listede olmayan durum çizilmez.
  const cardStatuses = useMemo(
    () => actionStatuses.data?.filter((s) => CARD_STATUS_IDS.includes(s.id)),
    [actionStatuses.data],
  )
  const items = groups.list.data?.content ?? []
  const totalElements = groups.list.data?.page.totalElements ?? 0

  // "Taslaklar" kartı: grup DEĞİL, tüm gruplardaki taslakların sabit girişi → arama grup adına göre
  // süzdüğünden aramada gösterilmez. Sayaç için yalnız toplam gerekir (size: 1).
  const searching = debounced.trim().length > 0
  const drafts = useDraftMeetings(0, 1)
  const draftCount = drafts.data?.page.totalElements
  // Kart yalnız sayaç GELDİKTEN sonra ve taslak varken çizilir (boş grup gibi boş kart gösterilmez; kartın
  // belirip kaybolması da önlenir).
  const showDraftCard = !searching && draftCount != null && draftCount > 0
  // Boş-durum, Taslaklar kartının da çizilmeyeceği kesinleşince gösterilir; aksi halde tek erişim noktası
  // olan kart yerine kısa süre "grup yok" görünürdü.
  const empty = items.length === 0 && !groups.list.isLoading && !drafts.isLoading && !showDraftCard

  return (
    <div className="page meetings-page">
      <PageHeader title={t('meetings.title')} />
      {/* `--standalone`: bu çubuğun altında TABLO yok (kart ızgarası var), dolayısıyla alt
          köşelerin kaynayacağı bir şey bulunmuyor → dört köşe yuvarlanır. */}
      <FilterBar
        className="ui-filterbar--standalone"
        searchValue={search}
        searchPlaceholder={t('meetings.search')}
        onSearch={setSearch}
        // İkisi de DÖNDÜRÜLÜR: Yenile'nin spinner'ı artık işleyicinin sözünü bekler, void dönseydi
        // hiç dönmezdi (bkz. RefreshButton). Taslak sayacı da kart grid'in parçası → o da tazelenir.
        onRefresh={() => Promise.all([groups.list.refetch(), drafts.refetch()])}
      />
      {empty ? (
        <EmptyState
          icon="pi pi-users"
          title={t('meetings.empty.title')}
          description={t('meetings.empty.desc')}
        />
      ) : (
        <>
          <div className="mg-grid">
            {items.map((g) => (
              <MeetingGroupCard
                key={g.id}
                group={g}
                actionStatuses={cardStatuses}
                onOpen={(grp) => navigate(`/minutes/meeting-minutes/meetings/group/${grp.id}`)}
              />
            ))}
            {/* Grup kartlarından sonra sabit Taslaklar kartı (grup değil; sayfalamaya dahil edilmez). */}
            {showDraftCard && (
              <DraftMeetingsCard
                count={draftCount}
                onOpen={() => navigate('/minutes/meeting-minutes/meetings/drafts')}
              />
            )}
          </div>
          {totalElements > PAGE_SIZE && (
            <Paginator
              first={page * PAGE_SIZE}
              rows={PAGE_SIZE}
              totalRecords={totalElements}
              onPageChange={(e) => setPage(e.page)}
            />
          )}
        </>
      )}
    </div>
  )
}
